import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  FaSearch,
  FaTimes,
  FaBox,
  FaCubes,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

// ============================================================
// Module-level session-persistent cache (Fix 1)
// Survives modal closes and re-opens within the same page session.
// ============================================================
const _tilesCache = { data: null, totalCount: 0, locationId: null, timestamp: 0 };

// Rank matches client-side (list is <=50) so name-prefix and word-prefix hits
// surface first, TallyPrime-style, without touching the shared GET /tiles sort.
const rankTilesForTerm = (list, term) => {
  const tokens = (term || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return list;

  const haystackOf = (tile) => [
    tile.tilesName, tile.displayName, tile.id, tile.partNumber,
    tile.brand?.name, tile.tilesCategory?.parameterName, tile.tilesType?.parameterName,
    tile.tilesFinishType?.parameterName, tile.tilesMM?.parameterName, tile.tilesInches?.parameterName
  ].filter(Boolean).join(' ').toLowerCase();

  const score = (tile) => {
    const name = (tile.tilesName || '').toLowerCase();
    const hay = haystackOf(tile);
    let s = 0;
    for (const t of tokens) {
      if (name.startsWith(t)) s += 12;
      if (hay.startsWith(t) || hay.includes(` ${t}`)) s += 8;
      if (hay.includes(t)) s += 2;
    }
    return s;
  };

  return [...list].sort((a, b) =>
    score(b) - score(a) || (a.tilesName || '').localeCompare(b.tilesName || '')
  );
};

// ============================================================
// SearchBar — Debounced search input
// ============================================================
const SearchBar = memo(({ onSearch, onClear, isLoading, darkMode, placeholder }) => {
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchValue.trim()) {
      debounceRef.current = setTimeout(() => onSearch(searchValue.trim()), 500);
    } else {
      onClear();
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchValue, onSearch, onClear]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <div className="w-full mb-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (searchValue.trim()) onSearch(searchValue.trim()); } }}
            placeholder={placeholder || 'Search tiles by name, ID, category, or brand...'}
            className={`w-full pl-10 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            autoComplete="off"
            autoFocus
          />
          {searchValue && (
            <button onClick={() => { setSearchValue(''); onClear(); inputRef.current?.focus(); }}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors duration-300 ${
                darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}>
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ============================================================
// Fix 5: Memoized tile card
// Only re-renders when isSelected, tile identity, or darkMode changes.
// Hover updates only re-render 2 cards (old + new selected) instead of all 6.
// ============================================================
const TileCard = memo(({ tile, idx, isSelected, onSelect, setSelectedIndex, darkMode, stock, netStock, showNetStock }) => {
  const totalStock = (stock.freshStockBox || 0) + (stock.freshStockPieces || 0) +
                     (stock.cornerDamagedBox || 0) + (stock.cornerDamagedPieces || 0) +
                     (stock.fullDamagedBox || 0) + (stock.fullDamagedPieces || 0);
  return (
    <div
      onClick={() => onSelect(tile)}
      className={`border-2 rounded-xl p-4 hover:border-blue-500 hover:shadow-md transition-all duration-200 cursor-pointer ${
        isSelected
          ? (darkMode ? 'bg-blue-900/30 border-blue-500 ring-2 ring-blue-500/20' : 'bg-blue-50 border-blue-500 ring-2 ring-blue-200')
          : (darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-white')
      }`}
      onMouseEnter={() => setSelectedIndex(idx)}
    >
      <div className="flex items-center mb-3">
        <div className="w-14 h-14 flex-shrink-0 mr-3">
          {tile.tilesImage || tile._id ? (
            <>
              <img
                src={`${process.env.REACT_APP_API_BASE_URL}/tiles/image/${tile._id}`}
                alt={tile.tilesName}
                className={`w-14 h-14 object-cover rounded-lg border transition-colors duration-300 ${
                  darkMode ? 'border-gray-600' : 'border-gray-200'
                }`}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className={`w-14 h-14 rounded-lg flex items-center justify-center border ${
                darkMode ? 'bg-gray-700 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-200'
              }`} style={{ display: 'none' }}>
                <FaBox size={18} />
              </div>
            </>
          ) : (
            <div className={`w-14 h-14 rounded-lg flex items-center justify-center border ${
              darkMode ? 'bg-gray-700 text-gray-400 border-gray-600' : 'bg-gray-100 text-gray-400 border-gray-200'
            }`}>
              <FaBox size={18} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm break-words ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            {tile.tilesName || tile.displayName || 'Unknown'}
          </h4>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {tile.tilesCategory?.parameterName || tile.id || 'N/A'}
          </p>
          {tile.tilesMM?.parameterName && (
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {tile.tilesMM.parameterName} MM
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        {tile.brand?.name && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {tile.brand.name}
          </span>
        )}
        {tile.tilesPiecesPerBox && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
            {tile.tilesPiecesPerBox} pcs/box
          </span>
        )}
        {tile.id && (
          <span className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {tile.id}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1 text-xs mb-1">
        <div className={`p-1.5 rounded ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
            Fresh Box: {stock.freshStockBox || 0}
          </span>
        </div>
        <div className={`p-1.5 rounded ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
            Fresh Pcs: {stock.freshStockPieces || 0}
          </span>
        </div>
        <div className={`p-1.5 rounded ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
            Corner Dmg Box: {stock.cornerDamagedBox || 0}
          </span>
        </div>
        <div className={`p-1.5 rounded ${darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
            Corner Dmg Pcs: {stock.cornerDamagedPieces || 0}
          </span>
        </div>
        <div className={`p-1.5 rounded ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
            Full Dmg Box: {stock.fullDamagedBox || 0}
          </span>
        </div>
        <div className={`p-1.5 rounded ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
          <span className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
            Full Dmg Pcs: {stock.fullDamagedPieces || 0}
          </span>
        </div>
      </div>

      {showNetStock && netStock && (
        <div className={`mt-1 p-2 rounded-lg border ${
          darkMode ? 'bg-cyan-900/20 border-cyan-800/40' : 'bg-cyan-50 border-cyan-200'
        }`}>
          <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
            Net Available Stock
          </p>
          <div className="grid grid-cols-1 gap-1 text-xs">
            <span className={darkMode ? 'text-cyan-300' : 'text-cyan-600'}>
              Fresh: {netStock.freshBoxes} box / {netStock.freshPieces} pcs
            </span>
            <span className={darkMode ? 'text-yellow-300' : 'text-yellow-600'}>
              Corner Dmg: {netStock.cornerDamagedBoxes} box / {netStock.cornerDamagedPieces} pcs
            </span>
            <span className={darkMode ? 'text-red-300' : 'text-red-600'}>
              Full Dmg: {netStock.fullDamagedBoxes} box / {netStock.fullDamagedPieces} pcs
            </span>
          </div>
        </div>
      )}

      {totalStock === 0 && !showNetStock && (
        <div className="text-xs text-red-500 text-center font-medium mt-1">Out of Stock</div>
      )}

      {isSelected && (
        <div className="mt-2 text-xs text-blue-600 font-medium text-center">
          Press Enter to select
        </div>
      )}
    </div>
  );
}, (prev, next) => prev.isSelected === next.isSelected && prev.tile._id === next.tile._id && prev.darkMode === next.darkMode);

// ============================================================
// TileSelectionModal — Shared across SO, DO, and other pages
// ============================================================
// Props:
//   isOpen          — boolean — show/hide the modal
//   onClose         — () => void
//   onSelect        — (tile) => void — callback when a tile is selected
//   darkMode        — boolean
//   userId          — string — current user id for API call
//   locationId      — string|null — if provided, can pass location filter + shows location stock
//   filterByLocation— boolean — if true, applies backend location filter when locationId exists
//   showNetStock    — boolean — if true, shows net stock calculation
// ============================================================
const TileSelectionModal = ({ isOpen, onClose, onSelect, darkMode, userId, locationId, filterByLocation = true, showNetStock = false }) => {
  const [tilesList, setTilesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalTiles, setTotalTiles] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(6);
  const [fetchedForLocation, setFetchedForLocation] = useState(null);
  const searchTermRef = useRef('');
  const listLenRef = useRef(0);
  const reqSeqRef = useRef(0);
  const abortRef = useRef(null);

  // Fetch tiles from API
  const fetchTiles = useCallback(async (searchTerm = '', { silent = false } = {}) => {
    const locKey = locationId || null;

    // Fix 2: Stale-while-revalidate — show cached data instantly, then silently refresh if stale (>15s)
    if (!searchTerm && !silent && _tilesCache.data && _tilesCache.locationId === locKey) {
      setTilesList(_tilesCache.data);
      setTotalTiles(_tilesCache.totalCount);
      setFetchedForLocation(locKey);
      listLenRef.current = _tilesCache.data.length;
      setPage(1);
      setSelectedIndex(0);
      if (Date.now() - _tilesCache.timestamp > 15000) {
        fetchTiles('', { silent: true });
      }
      return;
    }

    const seq = ++reqSeqRef.current;
    const isLatest = () => seq === reqSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Keep current results on screen while a new search loads; only show the
      // full spinner when there is nothing to display yet.
      if (!silent) setLoading(listLenRef.current === 0);
      searchTermRef.current = searchTerm;
      const queryParams = new URLSearchParams({
        userId: userId || '',
        search: searchTerm || '',
        limit: '50',
        status: 'active'
      });
      if (locationId && filterByLocation) {
        queryParams.set('location', locationId);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (!isLatest()) return;
      const rawTiles = Array.isArray(data) ? data : data.data || [];
      const serverTotalCount = data.totalCount || rawTiles.length;

      // Strip heavy image data to keep state light
      const processed = rankTilesForTerm(
        rawTiles.map(tile => {
          const clean = { ...tile };
          if (clean.brand?.image) delete clean.brand.image;
          if (clean.tilesImage) delete clean.tilesImage;
          if (clean.imageData) delete clean.imageData;
          return clean;
        }),
        searchTerm
      );

      setTilesList(processed);
      setTotalTiles(serverTotalCount);
      setFetchedForLocation(locKey);
      listLenRef.current = processed.length;

      // Fix 1: Update module-level cache (persists across modal opens within page session)
      if (!searchTerm) {
        _tilesCache.data = processed;
        _tilesCache.totalCount = serverTotalCount;
        _tilesCache.locationId = locKey;
        _tilesCache.timestamp = Date.now();
      }

      if (!silent) {
        setPage(1);
        setSelectedIndex(0);
      }
    } catch (error) {
      if (error?.name === 'AbortError' || !isLatest()) return;
      console.error('TileSelectionModal: Error fetching tiles:', error);
      if (!silent) { setTilesList([]); setTotalTiles(0); listLenRef.current = 0; }
    } finally {
      if (!silent && isLatest()) setLoading(false);
    }
  }, [userId, locationId, filterByLocation]);

  // Fix 3: Prefetch on component mount — data is ready before the user ever opens the modal
  useEffect(() => {
    fetchTiles('', { silent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fix 4: Reset page/selection when modal opens (DOM kept alive via CSS hide)
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSelectedIndex(0);
      fetchTiles('');
    }
  }, [isOpen, fetchTiles]);

  // Auto-refresh every 15 seconds while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      fetchTiles(searchTermRef.current, { silent: true });
    }, 15000);
    return () => clearInterval(interval);
  }, [isOpen, fetchTiles]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    const startIdx = (page - 1) * perPage;
    const tilesInPage = tilesList.slice(startIdx, startIdx + perPage);
    const count = tilesInPage.length;
    if (count === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex + 1 >= count && page * perPage < tilesList.length) {
        setPage(page + 1);
        setSelectedIndex(0);
      } else {
        setSelectedIndex(Math.min(selectedIndex + 1, count - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex - 1 < 0 && page > 1) {
        setPage(page - 1);
        setSelectedIndex(-1);
      } else {
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const tile = tilesInPage[selectedIndex];
      if (tile) onSelect(tile);
    }
  }, [page, perPage, tilesList, selectedIndex, onClose, onSelect]);

  // Fix -1 index (went to previous page)
  useEffect(() => {
    if (selectedIndex === -1) {
      const startIdx = (page - 1) * perPage;
      const tilesInPage = tilesList.slice(startIdx, startIdx + perPage);
      setSelectedIndex(Math.max(0, tilesInPage.length - 1));
    }
  }, [selectedIndex, page, perPage, tilesList]);

  // Helper: resolve stock for a tile at the target location
  const getLocationStock = (tile) => {
    const locStr = locationId?.toString?.() || locationId;
    const matchesLocation = fetchedForLocation === locStr;
    const defaults = { freshStockBox: 0, freshStockPieces: 0, damagedStockBox: 0, damagedStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 };

    if (tile.locationStock && typeof tile.locationStock === 'object' && matchesLocation) {
      return { ...defaults, ...tile.locationStock };
    }
    if (Array.isArray(tile.stock) && tile.stock.length > 0 && locationId) {
      const matched = tile.stock.find(s => {
        const stockLocId = s.location?._id?.toString?.() || s.location?.toString?.() || s.location;
        return stockLocId?.toString?.() === locStr;
      });
      if (matched) return { ...defaults, ...matched };
    }
    return defaults;
  };

  // Helper: compute aggregate total stock (across all locations)
  const getAggregateStock = (tile) => {
    const defaults = { freshStockBox: 0, freshStockPieces: 0, damagedStockBox: 0, damagedStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 };
    if (!Array.isArray(tile.stock) || tile.stock.length === 0) {
      return defaults;
    }
    return tile.stock.reduce((acc, s) => ({
      freshStockBox: acc.freshStockBox + (s.freshStockBox || 0),
      freshStockPieces: acc.freshStockPieces + (s.freshStockPieces || 0),
      damagedStockBox: acc.damagedStockBox + (s.damagedStockBox || 0),
      damagedStockPieces: acc.damagedStockPieces + (s.damagedStockPieces || 0),
      cornerDamagedBox: acc.cornerDamagedBox + (s.cornerDamagedBox || 0),
      cornerDamagedPieces: acc.cornerDamagedPieces + (s.cornerDamagedPieces || 0),
      fullDamagedBox: acc.fullDamagedBox + (s.fullDamagedBox || 0),
      fullDamagedPieces: acc.fullDamagedPieces + (s.fullDamagedPieces || 0)
    }), defaults);
  };

  // Helper: compute net stock
  // Net = (current stock + ordered + in-transit) - salesOrder
  const getNetStock = (tile) => {
    const stock = locationId ? getLocationStock(tile) : getAggregateStock(tile);
    const ordered = tile.boxesOrdered || 0;
    const inTransit = tile.inTransitBoxes || 0;
    const soFresh = tile.salesOrder?.freshBoxes || 0;
    const soCornerDamaged = tile.salesOrder?.cornerDamagedBoxes || 0;
    const soDamaged = tile.salesOrder?.damagedBoxes || 0;

    return {
      freshBoxes: (stock.freshStockBox || 0) + ordered + inTransit - soFresh,
      freshPieces: (stock.freshStockPieces || 0) - (tile.salesOrder?.freshPieces || 0),
      cornerDamagedBoxes: (stock.cornerDamagedBox || 0) - soCornerDamaged,
      cornerDamagedPieces: (stock.cornerDamagedPieces || 0) - (tile.salesOrder?.cornerDamagedPieces || 0),
      damagedBoxes: (stock.damagedStockBox || 0) - soDamaged,
      damagedPieces: (stock.damagedStockPieces || 0) - (tile.salesOrder?.damagedPieces || 0),
      fullDamagedBoxes: (stock.fullDamagedBox || 0),
      fullDamagedPieces: (stock.fullDamagedPieces || 0)
    };
  };

  // Fix 4: CSS hide instead of unmount — DOM stays alive between opens for instant re-display
  if (!isOpen) return null;

  const startIdx = (page - 1) * perPage;
  const tilesInPage = tilesList.slice(startIdx, startIdx + perPage);
  const totalPages = Math.ceil(tilesList.length / perPage);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden m-4 transition-colors duration-300 border-2 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b-2 ${
          darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <FaCubes className="text-white text-lg" />
            </div>
            <div>
              <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Select Tile</h3>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {tilesList.length < totalTiles ? `${tilesList.length} of ${totalTiles}` : totalTiles} tiles available
                <span className="ml-2 opacity-70">(↑↓ navigate, Enter select, Esc close)</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className={`p-5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <SearchBar
            onSearch={fetchTiles}
            onClear={fetchTiles}
            isLoading={loading}
            darkMode={darkMode}
            placeholder="Search tiles by name, ID, category, or brand..."
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-500 border-t-transparent"></div>
                <div className="absolute inset-0 animate-ping rounded-full h-10 w-10 border border-blue-300 opacity-30"></div>
              </div>
              <p className={`mt-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Searching tiles...</p>
            </div>
          ) : tilesInPage.length === 0 ? (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <FaCubes className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tiles found</p>
              {locationId === null && <p className="text-sm mt-1">Try a different search term</p>}
              {!locationId && locationId !== null && <p className="text-sm text-red-500 mt-2">Please select a location first</p>}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tilesInPage.map((tile, idx) => {
                const stock = locationId ? getLocationStock(tile) : getAggregateStock(tile);
                const netStock = showNetStock ? getNetStock(tile) : null;
                return (
                  <TileCard
                    key={tile._id}
                    tile={tile}
                    idx={idx}
                    isSelected={idx === selectedIndex}
                    onSelect={onSelect}
                    setSelectedIndex={setSelectedIndex}
                    darkMode={darkMode}
                    stock={stock}
                    netStock={netStock}
                    showNetStock={showNetStock}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`p-4 border-t-2 flex justify-between items-center ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing {startIdx + 1} to {Math.min(startIdx + perPage, tilesList.length)} of {tilesList.length} tiles
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPage(p => Math.max(p - 1, 1)); setSelectedIndex(0); }}
                disabled={page === 1}
                className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${
                  darkMode
                    ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700'
                    : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <FaChevronLeft />
              </button>
              <span className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => { setPage(p => Math.min(p + 1, totalPages)); setSelectedIndex(0); }}
                disabled={page >= totalPages}
                className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${
                  darkMode
                    ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700'
                    : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'
                }`}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TileSelectionModal;
